/**
 * Unified theme tokens for CMA and CWA (CBP-3939, ratified CBP-3947).
 *
 * Single source of truth for the design system. Pure data — this module must
 * never import react, react-native, or any styling runtime, so it stays safe
 * under Metro, Vite, Vitest, Jest, and Storybook alike.
 *
 * Primary color is vermillion across both apps (ratified CBP-3947). Semantic
 * palette scales (info/success/error/warning/gray) follow CWA values as the
 * authoritative source. Every divergence and its resolution is enumerated in
 * ./DECISIONS.md.
 *
 * All hex values are uppercase #RRGGBB (or the literal 'transparent');
 * the test suite enforces this.
 */
export const tokens = {
  colors: {
    brand: {
      orange: '#F67C01',
      blue: '#24358C',
      lightBlue: '#3045B6',
      green: '#009688',
      white: '#FFFFFF',
      warmWhite: '#F5F5ED',
      black: '#000000',
      transparent: 'transparent',
      midnight: '#293042',
      lightGrey: '#ECECE5',
      text: '#000000',
      textSoftBlack: '#21211F',
      paleBlue: '#7182DA',
      vermillion: '#E84931',
      lightVermillion: '#F6B6AD',
      darkVermillion: '#8B2C1D',
      textPlaceholder: '#C3C3BE',
      secondaryGrey: '#858581',
      selectedBlue: '#A7CEEB',
      selectedDarkBlue: '#3B5466',
      infoBlue: '#DCEBF7',
      darkGrey: '#3B3B3A',
      yellow: '#FFFF33',
      marigold: '#F8E6BB',
      darkMarigold: '#6C4D05',
      successDarkGreen: '#22562E',
    },
    // Vermillion scale — unified primary for both CMA and CWA (ratified CBP-3947).
    // The former blue CMA primary (300: #1B60F8) is deprecated; use brand.blue
    // for any explicit blue references.
    primary: {
      50: '#F5F5ED',
      100: '#FF9180',
      200: '#FE6E57',
      300: '#E84931',
      400: '#C7280F',
      500: '#C7280F',
      600: '#9E1600',
      700: '#9E1600',
      800: '#7D1100',
      900: '#520B00',
    },
    // Alias for primary (vermillion). Kept for backwards compatibility with
    // Phase 3 components that already target `accent.*`.
    accent: {
      50: '#F5F5ED',
      100: '#FF9180',
      200: '#FE6E57',
      300: '#E84931',
      400: '#C7280F',
      500: '#C7280F',
      600: '#9E1600',
      700: '#9E1600',
      800: '#7D1100',
      900: '#520B00',
    },
    secondary: {
      50: '#FFF0E0',
      100: '#FFE5C9',
      200: '#FFC991',
      300: '#FFAB53',
      400: '#E6933F',
      500: '#CD7C2A',
      600: '#AE6E2D',
      700: '#865727',
      800: '#5A3B1B',
      900: '#22170B',
    },
    tertiary: {
      50: '#DBFAEE',
      100: '#B9F3DC',
      200: '#89E3BE',
      300: '#50D3A0',
      400: '#3FAF83',
      500: '#2E8C68',
      600: '#266D52',
      700: '#1F4C3B',
      800: '#17352A',
      900: '#0F1F19',
    },
    // CWA values adopted as authoritative (ratified CBP-3947).
    info: {
      50: '#DBE1F1',
      100: '#DBE1F1',
      200: '#DBE1F1',
      300: '#38435C',
      400: '#38435C',
      500: '#38435C',
      600: '#38435C',
      700: '#38435C',
      800: '#293042',
      900: '#293042',
    },
    // CWA values adopted as authoritative (ratified CBP-3947).
    success: {
      50: '#D4F3D9',
      100: '#CBFAD6',
      200: '#D4F3D9',
      300: '#71E28A',
      400: '#21BA43',
      500: '#21BA43',
      600: '#21BA43',
      700: '#21BA43',
      800: '#298137',
      900: '#298137',
    },
    // CWA values adopted as authoritative (ratified CBP-3947).
    error: {
      50: '#F5E7E7',
      100: '#F5E7E7',
      200: '#F5E7E7',
      300: '#ED4C4C',
      400: '#ED4C4C',
      500: '#ED4C4C',
      600: '#ED4C4C',
      700: '#ED4C4C',
      800: '#901717',
      900: '#901717',
    },
    danger: {
      50: '#F5E7E7',
      100: '#F5E7E7',
      200: '#F5E7E7',
      300: '#ED4C4C',
      400: '#ED4C4C',
      500: '#ED4C4C',
      600: '#ED4C4C',
      700: '#ED4C4C',
      800: '#901717',
      900: '#901717',
    },
    // CWA values adopted as authoritative (ratified CBP-3947). Both apps
    // render warnings as subtle error toasts; scales are now identical.
    warning: {
      50: '#F5E7E7',
      100: '#F5E7E7',
      200: '#F5E7E7',
      300: '#ED4C4C',
      400: '#ED4C4C',
      500: '#ED4C4C',
      600: '#ED4C4C',
      700: '#ED4C4C',
      800: '#901717',
      900: '#901717',
    },
    warn: {
      50: '#F3F4BF',
      100: '#F3F4BF',
      200: '#F3F4BF',
      300: '#E6EA3B',
      400: '#E6EA3B',
      500: '#838612',
      600: '#838612',
      700: '#838612',
      800: '#838612',
      900: '#838612',
    },
    // CWA values adopted as authoritative (ratified CBP-3947).
    // ⚠ CMA: gray[100] was #F8F8F8 (near-white). It is now #C3C3BE (mid-grey,
    // matching brand.textPlaceholder). Mobile dark-mode Heading/Text components
    // that read gray[100] as a light foreground color will be visibly darker —
    // verify in CMA before releasing.
    gray: {
      50: '#FAFAFA',
      100: '#C3C3BE',
      200: '#E8E8E8',
      300: '#CCCCCC',
      400: '#A3A3A3',
      500: '#8B8B8B',
      600: '#696969',
      700: '#3F3F46',
      800: '#27272A',
      900: '#18181B',
    },
    infoGrey: {
      50: '#ECECE5',
      100: '#ECECE5',
      200: '#ECECE5',
      300: '#ECECE5',
      400: '#21211F',
      500: '#21211F',
      600: '#21211F',
      700: '#21211F',
      800: '#21211F',
      900: '#21211F',
    },
    lightBlue: {
      300: '#A7CEEB',
      500: '#647C8D',
    },
    lightWarn: {
      50: '#FFEFCB',
      100: '#FFEFCB',
      200: '#FFEFCB',
      300: '#FFEFCB',
      400: '#FFEFCB',
      500: '#665632',
      600: '#665632',
      700: '#665632',
      800: '#665632',
      900: '#665632',
    },
  },
  fontSizes: {
    '2xs': 11,
    xs: 14,
    sm: 16,
    md: 18,
    lg: 24,
    xl: 30,
    '2xl': 36,
    '3xl': 48,
    '4xl': 60,
    '5xl': 60,
    '6xl': 60,
    '7xl': 60,
    '8xl': 60,
    '9xl': 60,
  },
  fonts: {
    heading: 'Newsreader',
    body: 'Matter',
  },
} as const
